import React from 'react';
import PropTypes from 'prop-types';
import ProductCard from '../ProductCard';
import './collectionRow.css';

/* CollectionRow — one collection with its products visible (`.cl-row`).
   The React port of SP-main's `SF.collRow`; its only consumer is the Collections
   index, as in the reference.

   ⛔ Do not use this on the home page: Featured collections there is the SUMMARY
   level (a cover tile per kit) and Collection spotlight the DETAIL level.
   Rendering the summary as detail collapses two sections into one (done and
   reverted upstream, 2026-09-08).

   Five products and a count above them — both halves are needed, see
   collectionRow.css. */
export const COLL_ROW_PRODUCTS = 5;

export default function CollectionRow({
  name,
  description,
  count,
  products,
  href,
  onOpen,
  renderProduct,
  seeAllLabel,
}) {
  const shown = (products || []).slice(0, COLL_ROW_PRODUCTS);
  const n = typeof count === 'number' ? count : (products || []).length;

  return (
    <section className="cl-row">
      <div className="cl-rhead">
        <div>
          {/* h2, not h3: each row is a section of a page whose h1 is
              "Collections". The reference's own page still writes h3 in its empty
              state, which predates the heading-level pass. */}
          <h2>{name}</h2>
          {description && <p className="cl-rdesc">{description}</p>}
          <div className="cl-rcount">{n} product{n === 1 ? '' : 's'}</div>
        </div>

        {/* A real link, so it is keyboard-reachable and can be opened in a new
            tab; the router still intercepts the plain click. */}
        <a
          className="sf-seeall"
          href={href}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            if (onOpen) { e.preventDefault(); onOpen(e); }
          }}
        >
          {seeAllLabel} &rarr;
        </a>
      </div>

      <div className="cl-rprods">
        {shown.map((p, i) => (
          <React.Fragment key={p?.id ?? i}>
            {renderProduct ? renderProduct(p, i) : <ProductCard name={p?.name} />}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

CollectionRow.propTypes = {
  name: PropTypes.string.isRequired,
  /** The partner's own words about this collection. Omitted when empty. */
  description: PropTypes.string,
  /** How many products the collection holds. Defaults to `products.length`. */
  count: PropTypes.number,
  products: PropTypes.array,
  href: PropTypes.string,
  /** Router interception for the "See collection" link. */
  onOpen: PropTypes.func,
  /** Draws one product. The page owns the card's props and its routing. */
  renderProduct: PropTypes.func,
  seeAllLabel: PropTypes.string,
};

CollectionRow.defaultProps = { products: [], seeAllLabel: 'See collection' };
